/**
 * JurisdictionCoverageStatement — tells the landlord which rules Resida applied to this
 * lease / disclosure set / listing / notice. A quiet muted line for every coverage; the only
 * banner is for a state the rules library does not cover at all. Never blocks anything.
 */

import { Scale, TriangleAlert } from 'lucide-react';
import type { JurisdictionCoverage } from '@resida/api-types/lease-generation';
import {
  describeCoverageStatement,
  describeUncoveredWarning,
  type CoverageSubject,
} from '../lib/jurisdiction-coverage';

export interface JurisdictionCoverageStatementProps {
  coverage: JurisdictionCoverage | null | undefined;
  subject: CoverageSubject;
}

export function JurisdictionCoverageStatement({ coverage, subject }: JurisdictionCoverageStatementProps) {
  if (!coverage) return null;

  const warning = describeUncoveredWarning(coverage, subject);
  if (warning) {
    return (
      <div
        role="note"
        data-testid="jurisdiction-coverage-warning"
        className="grid grid-cols-[20px_minmax(0,1fr)] gap-2.5 rounded-lg border border-warning/25 bg-warning/10 px-3.5 py-3"
      >
        <TriangleAlert className="mt-px h-5 w-5 text-warning" aria-hidden="true" />
        <div>
          <p className="m-0 text-sm font-semibold text-warning">{warning.title}</p>
          <p className="m-0 mt-0.5 text-sm text-foreground">{warning.body}</p>
        </div>
      </div>
    );
  }

  const { applied, detail } = describeCoverageStatement(coverage);
  return (
    <p
      data-testid="jurisdiction-coverage-statement"
      className="m-0 flex items-start gap-1.5 text-sm text-muted-foreground"
    >
      <Scale className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
      <span className="min-w-0">
        <span className="font-medium text-foreground">{applied}</span>
        {detail ? ` ${detail}` : null}
      </span>
    </p>
  );
}
